/* @jsxImportSource @emotion/react */
import React from 'react'
import classNames from 'classnames'

import { ImgSanity } from '@slite/components'

import { HanddrawnArrows, HanddrawnQuotes } from './handdrawnArrows'
import type { Quote } from './SectionQuotes'
import { containerQueryMedium } from './style'
import { WebsiteText } from './WebsiteText'

type QuoteWithArrowsProps = {
  quote: Quote
  brandColor: string
  index: number
  className?: string
}

export function QuoteWithArrows({
  quote,
  brandColor,
  index,
  className,
}: QuoteWithArrowsProps) {
  return (
    // Wrapper owns the container context so the card's padding, type size and
    // arrows respond to their own width — the card can sit in a narrow column.
    <div className={className} css={{ containerType: 'inline-size' }}>
      <div
        className="background-full-white flexbox flexbox-column flexbox-align-center flexbox-gap-16 position-relative"
        css={{
          padding: 80,
          borderRadius: 32,
          border: '1px solid rgba(0, 0, 0, 0.05)',
          [containerQueryMedium]: {
            padding: 32,
          },
        }}
      >
        <WebsiteText
          variant="heroSubtitle"
          className="shade-00 text-align-center position-relative zindex-small"
          css={{
            maxWidth: 600,
            fontSize: 28,
            // Testimonials may carry intentional line breaks ("\n")
            whiteSpace: 'pre-line',
            [containerQueryMedium]: {
              fontSize: 26,
              lineHeight: 1.6,
            },
          }}
        >
          <span
            className={classNames('inline-block margin-right-8', brandColor)}
          >
            <HanddrawnQuotes side="start" />
          </span>
          {quote.testimonial}
          <span
            className={classNames('inline-block margin-left-8', brandColor)}
          >
            <HanddrawnQuotes side="end" />
          </span>
        </WebsiteText>
        <div className="flexbox flexbox-gap-16 flexbox-align-center margin-top-16 position-relative zindex-small">
          <ImgSanity
            className="radius-larger overflow-hidden"
            css={{
              height: 48,
              width: 48,
              objectFit: 'cover',
            }}
            src={quote.pictureUrl}
            alt={quote.name}
          />
          <div className="flexbox flexbox-column flexbox-gap-4">
            <WebsiteText variant="body" className="shade-00">
              {quote.name}
            </WebsiteText>
            <WebsiteText variant="body" className="shade-20">
              {quote.title}
            </WebsiteText>
          </div>
        </div>
        <div
          css={{
            position: 'absolute',
            bottom: index % 2 ? '0' : 'unset',
            top: index % 2 ? 'unset' : 0,
            left: 0,
            color: `var(--shade-40)`,
            [containerQueryMedium]: {
              display: 'none',
            },
          }}
        >
          <HanddrawnArrows side="left" row={index} />
        </div>
        <div
          css={{
            position: 'absolute',
            top: index % 2 ? '0' : 'unset',
            bottom: index % 2 ? 'unset' : 0,
            right: 0,
            color: `var(--shade-40)`,
            [containerQueryMedium]: {
              display: 'none',
            },
          }}
        >
          <HanddrawnArrows side="right" row={index} />
        </div>
      </div>
    </div>
  )
}
